import { useFormatting } from "@msys/ui";
import type { GridColDef } from "@mui/x-data-grid-premium";
import React from "react";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import { DataGridPriceEditCell } from "../cells/DataGridPriceEditCell.js";
import type { ModifyItemCalculationFn } from "../useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function PriceCell({ value }: { value: number }) {
  const { getFormattedPrice } = useFormatting();
  return <span>{getFormattedPrice(value)}</span>;
}

export function createWorkSellingRateColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "workSellingRate",
    headerName: options.headerName,
    width: 100,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) =>
      row.item?.proposedCalculation?.workSellingRate ?? 0,
    valueSetter: makeCalculationValueSetter<T>()(
      "workSellingRate",
      (itemId, value) =>
        options.modifyItemCalculation(itemId, {
          workSellingRate: value,
        })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <PriceCell value={params.value ?? 0} />;
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DataGridPriceEditCell {...params} />;
    },
  };
}
